import React from 'react';
import {
  Box, Button, ButtonText, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';

type OrientationRejectOverlayProps = {
  runState: OralExamRunStateResponse;
  onFixNow: () => void;
  onFinishFirst: () => void;
};

/**
 * Blocking gate when ID is rejected mid-orientation film (A1.6).
 * Absolute scrim + sibling content — not an AlertDialog.
 */
export function OrientationRejectOverlay({
  runState,
  onFixNow,
  onFinishFirst,
}: OrientationRejectOverlayProps) {
  const { t } = useTranslation();
  const { rejectionReasonCode, rejectionReason } = runState.idVerification ?? {};

  let reasonLabel: string;
  if (rejectionReasonCode) {
    reasonLabel = t(`oralExam.idReject.${rejectionReasonCode}`, {
      defaultValue: t('oralExam.idReject.fallback'),
    });
  } else {
    reasonLabel = rejectionReason ?? t('oralExam.idReject.fallback');
  }

  const note = rejectionReasonCode ? rejectionReason : null;

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      testID="oral-exam-orientation-reject-overlay"
      accessibilityLiveRegion="assertive"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="$bgDark"
        opacity={0.88}
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        justifyContent="center"
        alignItems="center"
        px="$4"
      >
        <VStack space="md" maxWidth={480} width="100%" alignItems="center">
          <Text size="xl" fontWeight="semibold" color="$textInverted" textAlign="center">
            {reasonLabel}
          </Text>
          {!!note && (
            <Text size="sm" color="$textOnDark" textAlign="center">
              {note}
            </Text>
          )}
          <Text size="md" color="$textOnDark" textAlign="center">
            {t('oralExam.lobby.orientation.reject.lead')}
          </Text>
          <Text size="sm" color="$textOnDark" textAlign="center">
            {t('oralExam.lobby.orientation.reject.paused')}
          </Text>
          <VStack space="sm" width="100%" maxWidth={320}>
            <Button
              size="sm"
              bg="$bgApp"
              onPress={onFixNow}
              testID="oral-exam-orientation-reject-fix-now"
            >
              <ButtonText color="$textHeading">
                {t('oralExam.lobby.orientation.reject.fixNow')}
              </ButtonText>
            </Button>
            <Button
              variant="outline"
              size="sm"
              borderColor="$textOnDark"
              onPress={onFinishFirst}
              testID="oral-exam-orientation-reject-finish-first"
            >
              <ButtonText color="$textOnDark">
                {t('oralExam.lobby.orientation.reject.finishFirst')}
              </ButtonText>
            </Button>
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
}
